import { useEffect, useRef, useCallback } from 'react';
import { db as dexieDB } from '../../../lib/db';
import { useHistoryCleanup } from '../../../lib/hooks/useHistoryCleanup';
import { type PresentationBackup } from '../../../lib/db';

interface UseAutoSaveProps {
  saveState: 'saved' | 'saving' | 'unsaved';
  isAutoSaveEnabled: boolean;
  isOffline: boolean;
  slides: any[];
  saveTemplate: (customPayload?: any, isAutoSave?: boolean) => Promise<any>;
  presentation: {
    isDirty: () => void;
    isClean: () => void;
    isSaving: () => void;
  };
  templateId: string | null;
  setTemplateId: (id: string | null) => void;
  rosTitle: string;
  layoutMode: 'square' | 'rectangle';
  teamOwnerId?: string;
}

interface UseAutoSaveReturn {
  retryCount: number;
  lastLocalSave: Date | null;
}

const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY = 5000; // 5 seconds
const AUTO_SAVE_DELAY = 0;
const LOCAL_STORAGE_DELAY = 0;

export default function useAutoSave({
  saveState,
  isAutoSaveEnabled,
  isOffline,
  slides,
  saveTemplate,
  presentation,
  templateId,
  setTemplateId,
  rosTitle,
  layoutMode,
  teamOwnerId
}: UseAutoSaveProps): UseAutoSaveReturn {
  const autoSaveDelayRef = useRef<NodeJS.Timeout | null>(null);
  const localStorageDelayRef = useRef<NodeJS.Timeout | null>(null);
  const saveInProgress = useRef<boolean>(false);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const retryCountRef = useRef<number>(0);
  const lastLocalSaveRef = useRef<Date | null>(null);
  const lastSaveAttemptRef = useRef<number>(0);
  const hasInitialSaveRef = useRef<boolean>(false);
  const restorationAttemptedRef = useRef<boolean>(false);

  // Use our new history cleanup hook
  useHistoryCleanup();

  // Reset saveInProgress flag if save state changes to 'saved'
  useEffect(() => {
    if (saveState === 'saved') {
      saveInProgress.current = false;
    }
  }, [saveState]);

  // Validate backup data structure
  const isValidBackup = (backup: PresentationBackup | undefined): backup is PresentationBackup => {
    if (!backup) return false;
    if (!backup.id) return false;
    if (!backup.slides || !Array.isArray(backup.slides)) return false;
    if (!backup.timestamp) return false;
    return true;
  };

  // Load and validate backup
  const loadBackup = async () => {
    try {
      // Skip if we've already attempted restoration or don't have a templateId
      if (restorationAttemptedRef.current || !templateId) {
        return;
      }

      restorationAttemptedRef.current = true;
      console.log('Auto-save: Checking for backup...');

      const backup = await dexieDB.getPresentation(templateId);

      // Validate backup
      if (!isValidBackup(backup)) {
        console.log('Auto-save: Invalid or missing backup data');
        return;
      }

      // At this point TypeScript knows backup is valid and not undefined
      console.log('Auto-save: Found valid backup from', backup.timestamp);

      // Only restore if we haven't already
      if (!hasInitialSaveRef.current) {
        console.log('Auto-save: Attempting to restore backup...');
        hasInitialSaveRef.current = true;

        try {
          const result = await saveTemplate({
            slides: backup.slides,
            rosTitle: backup.rosTitle || 'Untitled Presentation',
            layoutMode: backup.layoutMode || 'rectangle',
            teamOwnerId: backup.teamOwnerId,
            isRecovery: true,
            deletedSlides: Array.isArray(backup.deletedSlides) ? backup.deletedSlides : []
          }, true);

          if (result?.entity?.id) {
            console.log('Auto-save: Successfully restored backup');
            presentation.isClean();
            lastLocalSaveRef.current = new Date();
          } else {
            console.error('Auto-save: Restore failed - invalid result', result);
            // Clean up failed backup
            await dexieDB.deletePresentation(templateId);
          }
        } catch (saveError: any) {
          console.error('Auto-save: Failed to restore backup -', saveError?.message || saveError);
          // Clean up failed backup
          await dexieDB.deletePresentation(templateId);
        }
      } else {
        console.log('Auto-save: Backup restore skipped - already restored');
      }
    } catch (error) {
      console.error('Auto-save: Failed to check or restore from IndexedDB', error);
    }
  };

  // Step 4: Save to local storage with debouncing
  const saveToLocalStorage = useCallback(async () => {
    try {
      if (!templateId) return;

      // Clear any pending local storage save
      if (localStorageDelayRef.current) {
        clearTimeout(localStorageDelayRef.current);
      }

      // Debounce local storage saves
      localStorageDelayRef.current = setTimeout(async () => {
        const now = Date.now();
        // Only save if enough time has passed since last save
        if (now - (lastSaveAttemptRef.current || 0) < LOCAL_STORAGE_DELAY) {
          return;
        }
        lastSaveAttemptRef.current = now;

        const backupData = {
          id: templateId,
          slides,
          rosTitle: rosTitle || 'Untitled Presentation',
          layoutMode,
          teamOwnerId,
          timestamp: new Date().toISOString(),
          version: '1.0',
          deletedSlides: []
        };

        await dexieDB.savePresentation(backupData);
        lastLocalSaveRef.current = new Date();
        // console.log('Auto-save: Successfully saved to IndexedDB');
      }, LOCAL_STORAGE_DELAY);
    } catch (error) {
      console.error('Auto-save: Failed to save to IndexedDB', error);
    }
  }, [slides, rosTitle, templateId, layoutMode, teamOwnerId]);

  // Step 5: Save to server with retry logic
  const performServerSave = useCallback(async () => {
    // If we're in the middle of saving, skip this save
    if (saveInProgress.current) {
      console.log('Auto-save: Save already in progress, skipping');
      return;
    }

    // For new presentations, if we already have a save attempt in progress, skip
    if (templateId === null && hasInitialSaveRef.current) {
      console.log('Auto-save: Initial save already done for new presentation, skipping');
      return;
    }

    try {
      // console.log('Auto-save: Starting save', { isNewPresentation: templateId === null });
      saveInProgress.current = true;
      presentation.isSaving();

      const result = await saveTemplate(undefined, true);
      if (result?.entity?.id) {
        if (templateId === null) {
          // For new presentations, mark as having completed initial save
          hasInitialSaveRef.current = true;
          // console.log('Auto-save: Completed initial save for new presentation');
        }
        if (result.entity.id !== templateId) {
          // Only update template ID if it's different
          setTemplateId(result.entity.id);
        }
      }
      presentation.isClean();
      retryCountRef.current = 0; // Reset retry count on successful save
      saveInProgress.current = false; // Reset flag after successful save
      return result;
    } catch (error) {
      console.error('Auto-save: Server save failed', error);
      presentation.isDirty();

      // Step 9: Handle errors and retry
      if (retryCountRef.current < MAX_RETRY_ATTEMPTS && !isOffline) {
        retryCountRef.current++;
        // console.log(`Auto-save: Retry attempt ${retryCountRef.current} of ${MAX_RETRY_ATTEMPTS}`);

        if (retryTimeoutRef.current) {
          clearTimeout(retryTimeoutRef.current);
        }

        retryTimeoutRef.current = setTimeout(() => {
          saveInProgress.current = false; // Reset flag before retry
          performServerSave();
        }, RETRY_DELAY * retryCountRef.current); // Exponential backoff
      } else {
        console.error('Auto-save: Max retry attempts reached or offline');
        saveInProgress.current = false; // Reset flag if we're not retrying
      }
    }
  }, [saveTemplate, templateId, isOffline, setTemplateId, presentation]);

  // Step 3: Auto-save effect with debouncing - monitors for changes
  useEffect(() => {
    // For new presentations (templateId is null), we want to save once regardless of auto-save setting
    const isNewPresentation = templateId === null && !hasInitialSaveRef.current;

    // Don't auto-save if disabled (unless it's a new presentation) or no unsaved changes
    if ((!isAutoSaveEnabled && !isNewPresentation) || saveState !== 'unsaved') {
      return;
    }

    // Skip if a save is already in progress
    if (saveInProgress.current) {
      // console.log('Auto-save: Save in progress, skipping effect trigger');
      return;
    }

    // Clear existing timers
    if (autoSaveDelayRef.current) {
      clearTimeout(autoSaveDelayRef.current);
    }

    // Step 4: Save to local storage with debouncing
    saveToLocalStorage();

    // Set a delay before auto-saving to server
    autoSaveDelayRef.current = setTimeout(() => {
      // Only auto-save if we have unsaved changes and slides
      if (saveState === 'unsaved' && slides.length > 0) {
        // Don't attempt to save if offline
        if (isOffline) {
          return;
        }

        // Perform save for new presentation or regular auto-save
        performServerSave();
      }
    }, AUTO_SAVE_DELAY);

    return () => {
      if (autoSaveDelayRef.current) {
        clearTimeout(autoSaveDelayRef.current);
      }
    };
  }, [saveState, isAutoSaveEnabled, slides, isOffline, performServerSave, saveToLocalStorage, templateId]);

  // Step 7: Save on page exit
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (saveState === 'unsaved' && isAutoSaveEnabled) {
        // Save to local storage immediately
        saveToLocalStorage();
        performServerSave();

        // Try to send data via beacon API (works even after page unload)
        if (navigator.sendBeacon && templateId) {
          const payload = JSON.stringify({
            templateId,
            slides,
            rosTitle,
            layoutMode,
            teamOwnerId,
            timestamp: new Date().toISOString()
          });

          const blob = new Blob([payload], { type: 'application/json' });
          navigator.sendBeacon('/api/autosave-beacon', blob);
        }

        // Show browser confirmation dialog
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [saveState, isAutoSaveEnabled, saveToLocalStorage, performServerSave, templateId, slides, rosTitle, layoutMode, teamOwnerId]);

  // Clean up timers and refs on unmount
  useEffect(() => {
    return () => {
      if (autoSaveDelayRef.current) {
        clearTimeout(autoSaveDelayRef.current);
      }
      if (localStorageDelayRef.current) {
        clearTimeout(localStorageDelayRef.current);
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      // Reset all flags on unmount
      saveInProgress.current = false;
      retryCountRef.current = 0;
      hasInitialSaveRef.current = false;
      restorationAttemptedRef.current = false;
    };
  }, []);

  return {
    retryCount: retryCountRef.current,
    lastLocalSave: lastLocalSaveRef.current
  };
}
